import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
} from '@mui/material'

import BaseDialog from '../../BaseDialog'
import { hasPermission, useUserState } from '../../../context/user/UserContext'
import { Permissions } from '../../../entities/permissions'
import { useNotificationsProvider } from '../../../context/NotificationsContext'

export default function SaveQuickFilterDialog({
  open,
  onClose,
  onSave,
  initialValues,
}) {
  const { t } = useTranslation()
  const { user } = useUserState()
  const { showNotification } = useNotificationsProvider()

  const canManagePublic = hasPermission(
    Permissions.PAGE_QUICK_FILTERS_MANAGE_PUBLIC,
    user
  )

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [scope, setScope] = useState('private')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setName(initialValues?.name || '')
      setDescription(initialValues?.description || '')
      setScope(initialValues?.isPublic ? 'public' : 'private')
      setError('')
      setLoading(false)
    }
  }, [open, initialValues])

  const handleConfirm = async () => {
    if (!name.trim()) {
      setError('name_required')
      return
    }
    if (scope === 'public' && !canManagePublic) {
      setError('public_not_allowed')
      return
    }
    setLoading(true)
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        isPublic: scope === 'public',
      })
      showNotification('success', t('quick-filters.save-success'))
      onClose()
    } catch (err) {
      setLoading(false)
      showNotification('error', t('errors.api-error'))
    }
  }

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title={
        initialValues?.id
          ? t('quick-filters.edit-title')
          : t('quick-filters.save-title')
      }
      loading={loading}
    >
      <Grid container direction="column" spacing={3} sx={{ pt: 2 }}>
        <Grid item>
          <TextField
            variant="standard"
            required
            error={error === 'name_required'}
            id="quick-filter-name"
            label={t('quick-filters.name-label')}
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 120 }}
          />
        </Grid>
        <Grid item>
          <TextField
            variant="standard"
            id="quick-filter-description"
            label={t('quick-filters.description-label')}
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Grid>
        {canManagePublic && (
          <Grid item>
            <FormControl>
              <RadioGroup
                value={scope}
                onChange={(e) => setScope(e.target.value)}
              >
                <FormControlLabel
                  value="private"
                  control={<Radio />}
                  label={t('quick-filters.scope-private')}
                />
                <FormControlLabel
                  value="public"
                  control={<Radio />}
                  label={t('quick-filters.scope-public')}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        )}
      </Grid>
    </BaseDialog>
  )
}
